import React, { useState, useEffect, useContext } from "react";
import { RouteComponentProps } from "react-router-dom";
import { sub, format } from "date-fns";
import {
  Plan,
  SearchResultPlan,
  LowIncomeSubsidyStatus,
  ActionType,
} from "../../@types";
import { getPlan, getPlanCard, getPlanMetadata } from "../../api";
import {
  parsePlanLongIdIntoUrlPartForApi,
  logError,
  useTranslate,
} from "../../helpers";
import { PlanDetailsErrorPage } from "../PlanDetailsPage";

import { AppStore } from "../../app/store";
import { PlanCard } from "../../components";
import URI from "urijs";
import { PreviewContext } from "../../helpers";
import { printPage } from "../../components/PrintButton";
import { PlanDetailsCostsContainer } from "../PlanDetailsPage/components";
import { ApiError } from "../../helpers/errors";

interface PlanPreviewMatchParams {
  id: string;
}

interface PlanPreviewToken {
  planPreview?: string;
}

export const planPreviewTestId = "plan-preview-page";

const PlanPreviewPage: React.FunctionComponent<RouteComponentProps> = props => {
  const { location, match } = props;
  const t = useTranslate();
  const [planError, setPlanError] = useState(false);
  const [planCardError, setPlanCardError] = useState(false);
  const [plan, setPlan] = useState<undefined | Plan>(undefined);
  const [planCard, setPlanCard] = useState<undefined | SearchResultPlan>(
    undefined
  );
  const [fetchingPlan, setFetchingPlan] = useState(false);
  const [fetchingPlanCard, setFetchingPlanCard] = useState(false);
  const [versionDate, setVersionDate] = useState("");
  const {
    state: { lis },
    dispatch,
  } = useContext(AppStore.AppContext);

  const planPreviewHeader = {
    "hpms-plan-preview": (URI.parseQuery(location.search) as PlanPreviewToken)
      .planPreview,
  };

  const fetchPlan = async (planId: string): Promise<void> => {
    setFetchingPlan(true);
    try {
      const fetchedPlan = await getPlan(
        planId,
        lis
          ? {
              lis,
            }
          : {},
        planPreviewHeader
      );
      setPlan(fetchedPlan);
      setPlanError(false);
      setFetchingPlan(false);
    } catch (err) {
      setPlanError(true);
      setFetchingPlan(false);
      logError("Failed to retrieve plan (PlanPreviewPage)", err as ApiError);
    }
  };

  const fetchVersion = async (plan: Plan): Promise<void> => {
    const { name, contract_year: year } = plan;
    const errorMessage = `Could not get plan version date${
      name ? ` for ${name}` : ""
    }.`;
    if (!year) {
      setVersionDate("");
    } else {
      try {
        const planDataVersions = await getPlanMetadata();
        const planHash = planDataVersions.find(
          v => v.file === `${year}/plan/plan_hash.txt`
        );
        if (planHash) {
          const dataDate = sub(new Date(planHash.file_date), {
            days: 1,
          });
          setVersionDate(`${format(dataDate, "MM/dd/yyyy")} 8pm ET`);
        } else {
          const missingHash = `Missing plan hash: ${errorMessage}`;
          logError(missingHash, new Error(missingHash));
          setVersionDate("");
        }
      } catch (err) {
        logError(errorMessage, err as ApiError);
        setVersionDate("");
      }
    }
  };

  const fetchPlanCard = async (planId: string): Promise<void> => {
    setFetchingPlanCard(true);
    try {
      const planCard = await getPlanCard(
        planId,
        [],
        LowIncomeSubsidyStatus.LIS_NO_HELP,
        planPreviewHeader
      );
      setPlanCard(planCard);
      setPlanCardError(false);
      setFetchingPlanCard(false);
    } catch (err) {
      setPlanCardError(true);
      setFetchingPlanCard(true);
      logError("Failed to fetch plan card (PlanPreviewPage)", err as ApiError);
    }
  };

  let DetailsPage: React.ReactNode;
  if (plan) {
    DetailsPage = <PlanDetailsCostsContainer plan={plan} />;
  } else {
    DetailsPage = <h3>{t("app.loading")}</h3>;
  }

  let PlanCardElt: React.ReactNode;
  if (planCard) {
    PlanCardElt = (
      <PlanCard
        key={planCard.id}
        plan={planCard as SearchResultPlan}
        isCurrentPlan={false}
        {...props}
      />
    );
  } else {
    PlanCardElt = <h3>{t("app.loading")}</h3>;
  }

  // When the plan data is available, fetch the version
  useEffect(() => {
    if (plan) {
      fetchVersion(plan);
    }
  }, [plan]);

  // DOM-load useEffect
  useEffect(() => {
    dispatch({
      type: ActionType.RESET_STATE,
    });
    const planId = parsePlanLongIdIntoUrlPartForApi(
      (match.params as PlanPreviewMatchParams).id
    );
    if (planId && !fetchingPlan) {
      fetchPlan(planId);
    }
    if (planId && !fetchingPlanCard) {
      fetchPlanCard(planId);
    }

    const hpmsPrint = (event: MessageEvent): void => {
      const { origin, data } = event;

      const expectedOrigins = [
        "https://hpmstest.cms.gov",
        "https://hpmsimpl.cms.gov",
        "https://hpms.cms.gov",
      ];

      // This is debug code that can be removed after integration has been tested
      if (data === "hpmsPrint") {
        console.log(`Received hpmsPrint event from ${origin}.`);
      }

      if (expectedOrigins.includes(origin) && data === "hpmsPrint") {
        printPage();
      }
    };

    window.addEventListener("message", hpmsPrint, false);

    return (): void => {
      window.removeEventListener("message", hpmsPrint, false);
    };
  }, []);

  return !planError && !planCardError ? (
    <PreviewContext.Provider value={true}>
      <div className="planPreview appPage__content ds-u-margin-x--2 ds-u-sm-margin-x--7">
        <h1>{t("plan_preview.heading")}</h1>
        {versionDate && (
          <div className="ds-u-text-align--center">
            {t("plan_preview.data_as_of")} {versionDate}
          </div>
        )}
        <h2>{t("plan_preview.plan_card")}</h2>
        <div>{PlanCardElt}</div>
        <h2>{t("plan_preview.plan_details")}</h2>
        <div
          className={`e2e-${planPreviewTestId}`}
          data-testid={planPreviewTestId}
        >
          {DetailsPage}
        </div>
      </div>
    </PreviewContext.Provider>
  ) : (
    <PlanDetailsErrorPage />
  );
};

export default PlanPreviewPage;
